import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Typography,
  Button,
  styled,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
  Paper,
  Grid,
  InputAdornment,
} from "@mui/material";
import { FileImage, FileVideo, File, User, Phone, CalendarDays, Languages, PhoneCall } from "lucide-react";
import axios from "axios";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import AutohideSnackbar from "../../../components/AutohideSnackbar";
import axiosInstance from "../../../services/axiosInterceptors";
import { palette, theme as colorTheme } from "../../../Convoze.theme";



  const primary = colorTheme('palette', 'primary').value;
  const primaryBg = colorTheme('palette', 'primaryBg').value;
  const positive = colorTheme('palette', 'positive').value;
  const negative = colorTheme('palette', 'negative').value;
  const neutral = colorTheme('palette', 'neutral').value;


const LANGUAGE_OPTIONS = [
  { value: "hi-IN", label: "Hindi" },
  { value: "ur-IN", label: "Urdu" },
  { value: "gu-IN", label: "Gujarati" },
  { value: "bn-IN", label: "Bengali" },
  { value: "ml-IN", label: "Malayalam" },
  { value: "ta-IN", label: "Tamil" },
  { value: "te-IN", label: "Telugu" },
  { value: "mr-IN", label: "Marathi" },
  { value: "kn-IN", label: "Kannada" },
  { value: "pa-IN", label: "Punjabi" },
  { value: "en-IN", label: "English (Indian)" },
  { value: "or-IN", label: "Odia" },
  { value: "as-IN", label: "Assamese" },
];

const CALL_TYPE_OPTIONS = [
  { value: "New Call", label: "New Call" },
  { value: "Follow Up Call", label: "Follow Up Call" },
];

// Allowed file extensions
const allowedExtensions = [
  ".wav",
  ".mp3",
  ".m4a",
  ".mp4",
  ".aac",
  ".flac",
  ".ogg",
  ".wma",
  ".aiff",
  ".alac",
  ".opus",
];

// Styled components
const UploadBox = styled(Box)(({ theme, isDragging }) => ({
  border: "2px dashed rgba(99, 102, 241, 0.3)",
  borderRadius: 16,
  padding: theme.spacing(4),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: isDragging ? "rgba(99, 102, 241, 0.05)" : "white",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  minHeight: 100,
  "&:hover": {
    borderColor: primary,
    backgroundColor: "rgba(99, 102, 241, 0.05)",
  },
}));

const IconGroup = styled(Box)({
  display: "flex",
  gap: "4px",
  marginBottom: "24px",
});

const UploadButton = styled(Button)({
  backgroundColor: primary,
  borderRadius: "24px",
  padding: "10px 32px",
  textTransform: "none",
  fontSize: "16px",
  "&:hover": {
    backgroundColor: primary
  },
});

// File Upload Component
export default function FileUploadFormik() {
  const [isDragging, setIsDragging] = useState(false);
  const [reloadApi, setReloadApi] = useState(false);
  const fileInputRef = useRef(null);
  const [disable, setDisable] = useState(false);
  const token = localStorage.getItem("token");
  const [filesLength, setFilesLength] = useState(0);
  //snackbar
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [warningSnackbarOpen, setWarningSnackbarOpen] = useState(false);

  // Function to validate allowed files
  const validateFiles = (files) => {
    const validFiles = files.filter((file) =>
      allowedExtensions.includes(file.name.slice(file.name.lastIndexOf(".")))
    );

    if (validFiles.length !== files.length) {
      alert(
        "Some files have unsupported formats. Only .wav, .mp3, .m4a, .mp4, .aac, .flac, .ogg, .wma, .aiff, .alac, and .opus files are allowed."
      );
    }

    return validFiles;
  };

  // Function to upload files
  const uploadFiles = async (files, language, date, customerName, mobileNumber, callType) => {
    setFilesLength(files?.length);
    setDisable(true);
    if (!files || files.length === 0) {
      console.error("No files selected for upload");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    if (language) {
      formData.append("language", language);
    }
    if (date) {
      formData.append("date", date);
    }
    if (customerName) {
      formData.append("customerName", customerName);
    }
    if (mobileNumber) {
      formData.append("mobileNumber", mobileNumber);
    }
    if (callType) {
      formData.append("callType", callType);
    }

    try {
      const response = await axiosInstance.post(
        apiEndPointsConfig.upload,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setSnackbarMessage("Files uploaded successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setReloadApi(true);
      setDisable(false);
      
      formik.setFieldValue("files", []);
      formik.setFieldValue("customerName", "");
      formik.setFieldValue("mobileNumber", "");
      formik.setFieldValue("callType", "");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setFilesLength(0);
      setWarningSnackbarOpen(false);
    } catch (error) {
      setSnackbarMessage(error.response.data.status.value);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setDisable(false);
      console.error("Upload failed:", error.response.data.status.value);
    }
  };

  const todayStr = new Date().toISOString().split("T")[0];

  const validationSchema = Yup.object({
    files: Yup.array().min(1, "Please select at least one audio file"),
    language: Yup.string().required("Audio language is required"),
    date: Yup.string().required("JFW date is required"),
    customerName: Yup.string().trim().required("Customer name is required"),
    mobileNumber: Yup.string()
      .trim()
      .required("Mobile number is required")
      .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number"),
    callType: Yup.string().required("Call type is required"),
  });

  // Formik initialization
  const formik = useFormik({
    initialValues: {
      files: [],
      language: "",
      date: todayStr,
      customerName: "",
      mobileNumber: "",
      callType: "",
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: (data) => {
      if (data?.files?.length > 0) {
        setSnackbarMessage(
          `Uploading ${data.files.length} audio file${
            data.files.length > 1 ? "s" : ""
          }...`
        );
        setSnackbarSeverity("info");
        setSnackbarOpen(true);

        uploadFiles(data.files, data.language, data.date, data.customerName, data.mobileNumber, data.callType);
      }
    },
  });

  // Validates every field up front so a single click on an incomplete form
  // both highlights the offending fields and explains what's missing.
  const handleUploadClick = async (e) => {
    e.preventDefault();
    const errors = await formik.validateForm();
    const touchedAll = Object.keys(formik.initialValues).reduce(
      (acc, key) => ({ ...acc, [key]: true }),
      {}
    );
    formik.setTouched(touchedAll, false);

    if (Object.keys(errors).length > 0) {
      setSnackbarMessage(Object.values(errors)[0]);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    formik.handleSubmit();
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Checks selected file names against the CallRecording collection so a
  // duplicate is caught the moment it's picked, instead of only surfacing as
  // an upload error after the whole file has already been sent.
  const checkForDuplicateFiles = async (fileNames) => {
    try {
      const res = await axiosInstance.post(
        apiEndPointsConfig.checkDuplicateFileNames,
        { fileNames },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res?.data?.data?.duplicates || [];
    } catch (error) {
      console.error("Error checking duplicate files", error);
      return [];
    }
  };

  const applySelectedFiles = async (rawFiles) => {
    const validFiles = validateFiles(rawFiles);
    if (!validFiles.length) {
      setFilesLength(0);
      formik.setFieldValue("files", []);
      return;
    }

    const duplicateNames = await checkForDuplicateFiles(validFiles.map((file) => file.name));
    const uniqueFiles = validFiles.filter((file) => !duplicateNames.includes(file.name));

    if (duplicateNames.length) {
      setSnackbarMessage(`These files already exist and were not added: ${duplicateNames.join(", ")}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }

    setFilesLength(uniqueFiles.length);
    formik.setFieldValue("files", uniqueFiles);

    // Show warning if 10 or more files selected
    if (uniqueFiles.length >= 10) {
      setWarningSnackbarOpen(true);
    }
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    applySelectedFiles(files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    applySelectedFiles(files);
  };

  const showError = (field) =>
    (formik.touched[field] || formik.submitCount > 0) && Boolean(formik.errors[field]);
  const errorText = (field) => (showError(field) ? formik.errors[field] : "");

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto" }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 4,
          border: "1px solid #EFEAFB",
          boxShadow: "0 4px 24px rgba(118, 53, 221, 0.06)",
        }}
      >
      <form onSubmit={handleUploadClick} noValidate>
        <UploadBox
          isDragging={isDragging}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label="Upload files"
        >
          <IconGroup>
            <FileImage size={32} color={primary} />
            <File size={32} color={primary}/>
            <FileVideo size={32} color={primary}/>
          </IconGroup>

          <Typography variant="h5" component="div" sx={{ mb: 1 }}>
            Drag & drop{" "}
            <Box component="span" sx={{ color:primary }}>
              audio&nbsp;
            </Box>
            <Box component="span" sx={{ color: primary }}>
              files
            </Box>
            , or any{" "}
            <Box component="span" sx={{ color:primary }}>
              file
            </Box>
          </Typography>
          {formik.values.files.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: positive,
                fontWeight: 500,
                mb: 1,
              }}
            >
              {formik.values.files.length} audio file
              {formik.values.files.length > 1 ? "s" : ""} selected
            </Typography>
          )}
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            or{" "}
            <Box
              component="span"
              sx={{
                color: primary,
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              browse files
            </Box>{" "}
            on your computer
          </Typography>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInput}
            style={{ display: "none" }}
            multiple
          />
        </UploadBox>
        {showError("files") && (
          <FormHelperText error sx={{ mt: 0.5, ml: 1.5 }}>
            {formik.errors.files}
          </FormHelperText>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={showError("language")}>
              <InputLabel id="audio-language-label" shrink>Audio Language *</InputLabel>
              <Select
                labelId="audio-language-label"
                id="audio-language"
                value={formik.values.language}
                label="Audio Language *"
                displayEmpty
                onChange={(e) => formik.setFieldValue("language", e.target.value)}
                onBlur={() => formik.setFieldTouched("language", true)}
                renderValue={(selected) => {
                  if (!selected) {
                    return <Box component="span" sx={{ color: "text.disabled" }}>Select audio language</Box>;
                  }
                  return LANGUAGE_OPTIONS.find((option) => option.value === selected)?.label || selected;
                }}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <Languages size={18} color={primary} />
                  </InputAdornment>
                }
              >
                {LANGUAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {showError("language") && (
                <FormHelperText>{formik.errors.language}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="date"
              label="JFW Date *"
              value={formik.values.date}
              onChange={(e) => formik.setFieldValue("date", e.target.value)}
              onBlur={() => formik.setFieldTouched("date", true)}
              error={showError("date")}
              helperText={errorText("date")}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarDays size={18} color={primary} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Customer Name *"
              placeholder="Enter customer name"
              value={formik.values.customerName}
              onChange={(e) => formik.setFieldValue("customerName", e.target.value)}
              onBlur={() => formik.setFieldTouched("customerName", true)}
              error={showError("customerName")}
              helperText={errorText("customerName")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <User size={18} color={primary} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Mobile Number *"
              placeholder="10-digit mobile number"
              value={formik.values.mobileNumber}
              onChange={(e) => formik.setFieldValue("mobileNumber", e.target.value)}
              onBlur={() => formik.setFieldTouched("mobileNumber", true)}
              error={showError("mobileNumber")}
              helperText={errorText("mobileNumber")}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone size={18} color={primary} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={showError("callType")}>
              <InputLabel id="call-type-label" shrink>Call Type *</InputLabel>
              <Select
                labelId="call-type-label"
                id="call-type"
                value={formik.values.callType}
                label="Call Type *"
                displayEmpty
                onChange={(e) => formik.setFieldValue("callType", e.target.value)}
                onBlur={() => formik.setFieldTouched("callType", true)}
                renderValue={(selected) => {
                  if (!selected) {
                    return <Box component="span" sx={{ color: "text.disabled" }}>Select call type</Box>;
                  }
                  return CALL_TYPE_OPTIONS.find((option) => option.value === selected)?.label || selected;
                }}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <PhoneCall size={18} color={primary} />
                  </InputAdornment>
                }
              >
                {CALL_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {showError("callType") && (
                <FormHelperText>{formik.errors.callType}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 3,
          }}
        >
          {disable == false ? (
            <UploadButton
              type="submit"
              variant="contained"
              sx={{
                bgcolor: primary,
                color: "white",
                textTransform: "none",
                fontSize: "1rem",
                height: "44px",
                borderRadius: "12px",
                boxShadow: "0 6px 16px rgba(118, 53, 221, 0.25)",
                "&:hover": {
                  bgcolor: "#5f2ab8",
                },
              }}
            >
              Upload
            </UploadButton>
          ) : (
            <UploadButton
              type="submit"
              variant="contained"
              disabled={true}
              sx={{
                bgcolor: primary,
                color: "white",
                textTransform: "none",
                fontSize: "1rem",
                height: "44px",
                borderRadius: "12px",
                "&:hover": {
                  bgcolor: primary,
                },
              }}
            >
              Uploading{" "}
              {formik.values.files.length > 0 &&
                `(${formik.values.files.length})`}{" "}
              &nbsp;&nbsp;
              <CircularProgress size={20} sx={{ color: "white" }} />
            </UploadButton>
          )}
        </Box>
      </form>
      </Paper>
      {/* Snackbar Component */}
      <AutohideSnackbar
        message={snackbarMessage}
        severity={snackbarSeverity}
        open={snackbarOpen}
        fileCount={filesLength}
        onClose={() => setSnackbarOpen(false)}
      />
      <AutohideSnackbar
        message="You're uploading 10 or more audio files. This might take some time."
        open={warningSnackbarOpen}
        onClose={() => setWarningSnackbarOpen(false)}
        severity="warning"
      />
    </Box>
  );
}
